/**
 * violationsStore
 *
 * Holds the CURRENTLY DISPLAYED violations + selection state. App.tsx
 * pushes engine-computed violations in via `setActive` whenever the
 * engine's inputs change (rules, elements, enabled jurisdictions).
 * Every other component in the app (ViolationsPanel, ElementInspector,
 * SheetViewer) reads from this single store, so selection state is
 * preserved across re-evaluations.
 *
 * History: a pre-computed `loadFromUrl` path + `staticCache` lived here
 * until 2026-05-18 to support the "Pre-computed vs Live" demo toggle.
 * Both paths produced the same violations after mocks were removed, so
 * the toggle added a concept (two modes) for no functional gain. The
 * static path was deleted; the engine is now the only source.
 */
import type { Violation } from "@vera/shared";
import { create } from "zustand";

interface ViolationsStore {
  /** Full engine + geometry violation set (unfiltered). */
  violations: Violation[];

  selectedViolationId: string | null;
  loaded: boolean;
  loadError: string | null;

  setActive: (v: Violation[]) => void;
  selectViolation: (id: string | null) => void;
}

export const useViolationsStore = create<ViolationsStore>((set, get) => ({
  violations: [],
  selectedViolationId: null,
  loaded: false,
  loadError: null,

  setActive: violations => {
    const currentSel = get().selectedViolationId;
    const stillThere =
      currentSel !== null && violations.some(v => v.id === currentSel);
    set({
      violations,
      loaded: true,
      selectedViolationId: stillThere ? currentSel : null,
    });
  },

  selectViolation: id => set({ selectedViolationId: id }),
}));

export function getSelectedViolation(state: ViolationsStore): Violation | null {
  if (!state.selectedViolationId) return null;
  return state.violations.find(v => v.id === state.selectedViolationId) ?? null;
}
